import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { feedbackApi, feedbackQueryKeys } from "../../shared/api/feedback";
import { formatDateTime } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { ReportErrorState } from "../../shared/ui/ReportErrorState";
import { Card, CardContent } from "@/components/ui/card";

function Stars({ n }: { n: number }) {
  return (
    <span className="text-amber-500" aria-label={`${n} sao`}>
      {"★".repeat(n)}
      <span className="text-muted-foreground">{"★".repeat(Math.max(0, 5 - n))}</span>
    </span>
  );
}

/** Đánh giá bệnh nhân — admin summary + recent list. */
export function FeedbackPage() {
  const summary = useQuery({
    queryKey: feedbackQueryKeys.summary(),
    queryFn: () => feedbackApi.summary(),
  });
  const recent = useQuery({
    queryKey: feedbackQueryKeys.recent(),
    queryFn: () => feedbackApi.recent(100),
  });

  const s = summary.data;
  const maxBar = s ? Math.max(1, ...s.histogram) : 1;

  return (
    <div>
      <PageHeader
        title="Đánh giá của bệnh nhân"
        description="Điểm hài lòng sau khám (1–5 sao) và nhận xét."
      />

      {/* #19850 — lỗi từng hiện thành "—", "0 lượt đánh giá" và "Chưa có đánh giá nào". */}
      {(summary.isError || recent.isError) && (
        <div className="mb-4">
          <ReportErrorState
            inline
            error={summary.error ?? recent.error}
            what="đánh giá của bệnh nhân"
            onRetry={() => {
              void summary.refetch();
              void recent.refetch();
            }}
          />
        </div>
      )}
      <div className="grid gap-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="text-4xl font-bold text-foreground">
                {s ? s.average.toFixed(1) : "—"}
              </div>
              <div>
                <div className="text-amber-500 text-lg">
                  <Stars n={s ? Math.round(s.average) : 0} />
                </div>
                <div className="text-sm text-muted-foreground">{s?.count ?? 0} lượt đánh giá</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="grid gap-1.5 p-5">
              {(s?.histogram ?? [0, 0, 0, 0, 0]).map((cnt, i) => {
                const star = 5 - i;
                return (
                  <div key={star} className="flex items-center gap-2 text-sm">
                    <span className="w-8 tabular-nums text-muted-foreground">{star}★</span>
                    <span className="h-2 flex-1 overflow-hidden rounded bg-muted">
                      <span
                        className="block h-full bg-amber-400"
                        style={{ width: `${(cnt / maxBar) * 100}%` }}
                      />
                    </span>
                    <span className="w-8 text-right tabular-nums text-muted-foreground">{cnt}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Đánh giá gần đây</h3>
            {recent.isError ? (
              <p className="text-sm text-muted-foreground">—</p>
            ) : (recent.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Chưa có đánh giá nào.</p>
            ) : (
              <ul className="grid gap-3">
                {(recent.data ?? []).map((f) => (
                  <li key={f.id} className="border-b pb-3 last:border-b-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Stars n={f.rating} />
                      <Link
                        className="font-medium text-primary hover:underline"
                        to={`/patients/${f.patientId}`}
                      >
                        {f.patientName}
                      </Link>
                      <span className="text-xs text-muted-foreground">{formatDateTime(f.at)}</span>
                    </div>
                    {f.comment && <div className="mt-1 text-sm text-foreground">{f.comment}</div>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
